import { navigate } from '@/utils/NavigationUtils'
import type { Policy as ApiPolicy } from 'policies-api'
import { DDSLine, Status } from 'policies-api'
import { usePoliciesStore } from '@/stores/policiesStore'
import { getPartnerProxyId } from '@/utils/KeycloakUtils'
import type { PolicyWithIconAndTitles } from 'de-zurich-ui-library'
import type { ContractBase } from '@/types/policy/PolicyTypes'
import { isAuto, isKrad } from '@/utils/motor/MotorUtilsCommon'

/**
 * Checks if a policy status should be treated as active.
 * NON_CONTRIBUTORY_B and NON_CONTRIBUTORY_C are treated as active according to KPTWO-5823.
 *
 * @param policyStatus - The policy status to check
 * @returns True if the status should be treated as active
 */
export function isActivePolicyStatus(policyStatus: string | undefined): boolean {
  return (
    policyStatus === Status.Active ||
    policyStatus === 'NON_CONTRIBUTORY_B' ||
    policyStatus === 'NON_CONTRIBUTORY_C'
  )
}

export function mapContractBase(base: ContractBase, policy: ApiPolicy): void {
  policy.policyStatus = base.status
  policy.policyId = `${base.companyCode}-${base.policyNumber}`
  policy.policyNumber = base.policyNumber
  policy.beginningDatePolicy = base.startOfContract
  policy.expiryDatePolicy = base.endOfContract
  policy.insuranceCompanyCode = base.companyCode
  policy.modificationDatePolicy = base.lastModified
}

export function convertToKebabCase(input: string): string {
  return input
    ? input
        .replaceAll(/([a-z])([A-Z])/g, '$1-$2')
        .replaceAll(/\s+/g, '-')
        .toLowerCase()
    : ''
}

// from (ISO) Date to DE-Date
export function formatDate(dateString: string | Date | null | undefined): string {
  if (!dateString) return ''
  let t_dateString: string
  if (typeof dateString === 'string') {
    t_dateString = dateString
  } else {
    t_dateString = dateString.toISOString()
  }
  // iso: 2010-02-20T00:00:00+01:00
  const isoRegex = /^\d*-\d*-\d*(T.*)?$/
  if (isoRegex.exec(t_dateString)) {
    const shortDate = t_dateString.slice(0, 10)
    const dateArr = shortDate.split('-')
    return dateArr[2] + '.' + dateArr[1] + '.' + dateArr[0]
  }
  return t_dateString
}

export function statusClassesForPolicy(policy: ApiPolicy): string {
  if (policy?.policyStatus) {
    const policyStatus = policy.policyStatus
    switch (policyStatus) {
      case Status.Active:
      case Status.ShortTerm:
      case 'NON_CONTRIBUTORY_B':
      case 'NON_CONTRIBUTORY_C':
        // NON_CONTRIBUTORY_B and NON_CONTRIBUTORY_C should be styled as active (green)
        // according to KPTWO-5823: These statuses should be counted as active and styled accordingly
        return 'status' // default case is green
      case Status.Canceled:
        return 'status status--deleted' // red
      case Status.Submitted:
        return 'status status--processing' // orange
      default:
        return 'status status--cancelled' // grey
    }
  }
  return 'status status--cancelled' // grey
}

export function goToDetailView(policy: ApiPolicy, detailUrl?: string): void {
  const t_detailUrl = detailUrl ?? usePoliciesStore().policyDetailURL

  if (!policy?.ddsLines) return

  const line: string =
    policy.ddsLines?.length === 1
      ? convertToKebabCase(policy.ddsLines[0]!)
      : 'bundled-property'
  const finalDetailUrl: string = t_detailUrl
    .replace('<LINE>', line)
    .replace('<POLICY_ID>', policy.policyId)
    .replace('<PARTNER_ADDRESS_ID>', policy.partnerAddressIDVN?.toString() ?? '')

  navigate(finalDetailUrl)
}

export async function selfServiceListOnMounted(): Promise<
  ApiPolicy[] | PolicyWithIconAndTitles[]
> {
  const partnerProxyId: string | null = getPartnerProxyId()
  if (!partnerProxyId) {
    throw new Error('No partner proxy id available!')
  }

  let shallowPolicyList: ApiPolicy[] = []
  let shallowPolicyIconTitlesList: PolicyWithIconAndTitles[] | null = []

  try {
    //fetch policies list
    shallowPolicyList =
      await usePoliciesStore().getAllPoliciesByPartnerProxyId(partnerProxyId)
  } catch (error_) {
    console.error('fetch shallowPolicyList failed', error_)
    return []
  }

  if (!shallowPolicyList || shallowPolicyList.length === 0) return []

  // new icon & title resolver
  // Keep this tenant-specific import lazy because its policy-tile dependencies import PolicyUtils.
  const { addIconsAndTitlesToAllContracts } =
    await import('@/utilsTenant/policy/TitleAndIconResolver')
  shallowPolicyIconTitlesList = await addIconsAndTitlesToAllContracts(shallowPolicyList)

  if (!shallowPolicyIconTitlesList) {
    console.error('addIconsAndTitlesToAllContracts failed')
    return []
  }

  return shallowPolicyIconTitlesList
}

async function selfServiceListMotorContractsOnMounted(
  wkzFilter?: (wkz: string) => boolean
): Promise<ApiPolicy[] | PolicyWithIconAndTitles[]> {
  const partnerProxyId: string | null = getPartnerProxyId()
  if (!partnerProxyId) {
    throw new Error('No partner proxy id available!')
  }

  let shallowPolicyList: ApiPolicy[] = []

  try {
    //fetch policies list
    shallowPolicyList =
      await usePoliciesStore().getAllPoliciesByPartnerProxyId(partnerProxyId)
  } catch (error_) {
    console.error('fetch shallowPolicyList failed', error_)
    return []
  }

  if (!shallowPolicyList || shallowPolicyList.length === 0) return []

  // new icon & title resolver
  // Keep this tenant-specific import lazy because its policy-tile dependencies import PolicyUtils.
  const { addIconsAndTitlesToMotorContracts } =
    await import('@/utilsTenant/policy/TitleAndIconResolver')
  const shallowPolicyIconTitlesList = await addIconsAndTitlesToMotorContracts(
    shallowPolicyList,
    wkzFilter
  )

  if (!shallowPolicyIconTitlesList) {
    console.error('addIconsAndTitlesToAllContracts failed')
    return []
  }

  return shallowPolicyIconTitlesList
}

export async function selfServiceListMotorOnlyOnMounted(): Promise<
  ApiPolicy[] | PolicyWithIconAndTitles[]
> {
  return selfServiceListMotorContractsOnMounted()
}

export async function selfServiceListAutoOnlyOnMounted(): Promise<
  ApiPolicy[] | PolicyWithIconAndTitles[]
> {
  return selfServiceListMotorContractsOnMounted((wkz) => isAuto(wkz))
}

export async function selfServiceListByDDSLinesOnMounted(
  ddsLine: DDSLine,
  inclBundle: boolean = true
): Promise<ApiPolicy[] | PolicyWithIconAndTitles[]> {
  const allPolicies = await selfServiceListOnMounted()
  // filter() on a union of array types collapses the element type to
  // (ApiPolicy | PolicyWithIconAndTitles)[]; cast back to the declared
  // union-of-arrays type, since the underlying array is always
  // homogeneous at runtime.

  if (inclBundle) {
    return allPolicies.filter((policy) => policy.ddsLines?.includes(ddsLine)) as
      ApiPolicy[] | PolicyWithIconAndTitles[]
  }

  // Only return policies that have EXCLUSIVELY this ddsLine (not just among others).
  return allPolicies.filter(
    (policy) => policy.ddsLines?.length === 1 && policy.ddsLines[0] === ddsLine
  ) as ApiPolicy[] | PolicyWithIconAndTitles[]
}

/**
 * Entfernt Duplikate aus mehreren Policen-Listen anhand der policyId.
 * Wird benötigt, weil z.B. Bundle-Verträge (Motor + LegalProtection) sowohl über
 * den Auto/Krad-WKZ-Filter als auch über einen DDSLine-Filter gefunden werden
 * können und sonst doppelt in der zusammengeführten Liste auftauchen würden.
 */
function mergeUniquePoliciesByPolicyId(
  ...policyLists: (ApiPolicy[] | PolicyWithIconAndTitles[])[]
): ApiPolicy[] | PolicyWithIconAndTitles[] {
  const seenPolicyIds = new Set<string>()
  const merged: (ApiPolicy | PolicyWithIconAndTitles)[] = []

  for (const policies of policyLists) {
    for (const policy of policies) {
      if (seenPolicyIds.has(policy.policyId)) continue
      seenPolicyIds.add(policy.policyId)
      merged.push(policy)
    }
  }

  return merged as ApiPolicy[] | PolicyWithIconAndTitles[]
}

export interface SelfServiceListConfig {
  /** Zusätzlich zu Auto (WKZ 112) auch Krad-Verträge (Motorrad, Roller, Trike, ...) einschließen. */
  includeKrad?: boolean
  /** Zusätzlich zu den Motor-Verträgen auch Verträge dieser DDSLine(s) einschließen (z.B. 'LegalProtection'). */
  includeDDSLines?: DDSLine[]
}

/**
 * Allgemeine, konfigurierbare Selfservice-Listen-Funktion für Self-Services, die auf
 * Auto-Verträgen basieren und optional per Feature-Flag um weitere Vertragsarten
 * (Krad und/oder beliebige DDSLines) erweitert werden können, z.B. für
 * "Digitale Karte anfordern".
 *
 * Immer enthalten: Auto-Verträge (WKZ 112, siehe isAuto).
 * Optional: Krad-Verträge (config.includeKrad) und/oder Verträge bestimmter
 * DDSLines wie z.B. 'LegalProtection' (config.includeDDSLines) - beliebig kombinierbar.
 *
 * Die Ergebnislisten werden zusammengeführt und über die policyId dedupliziert
 * (siehe mergeUniquePoliciesByPolicyId), damit Bundle-Verträge nicht doppelt erscheinen.
 */
export async function selfServiceListConfiguredOnMounted(
  config: SelfServiceListConfig = {}
): Promise<ApiPolicy[] | PolicyWithIconAndTitles[]> {
  const { includeKrad = false, includeDDSLines = [] } = config

  const motorPolicies = await selfServiceListMotorContractsOnMounted(
    (wkz) => isAuto(wkz) || (includeKrad && isKrad(wkz))
  )

  if (includeDDSLines.length === 0) {
    return motorPolicies
  }

  const ddsLinePolicyLists = await Promise.all(
    includeDDSLines.map((ddsLine) => selfServiceListByDDSLinesOnMounted(ddsLine))
  )

  return mergeUniquePoliciesByPolicyId(motorPolicies, ...ddsLinePolicyLists)
}
